import { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  getPaginationRowModel,
  columnResizeMode,
} from '@tanstack/react-table';
import Button from '../button';
import Dropdown from '../dropdown';
import { swapRootRowsInData, swapRowsInData } from './tableUtil';
import IndeterminateCheckbox from '../reusable_table/IndeterminateCheckbox';

const EMPTY_PINNING = {};

function SimpleTable({
  data,
  isLast = false,
  isFirst = false,
  columns,
  selectable = false,
  draggable = false,
  hasExpand = false,
  fetchNextPage = () => {},
  fetchPreviousPage = () => {},
  canPreviousPage = false,
  defaultHidden = {},
  pageIndex = 0,
  pageSize = 20,
  showColumnSelector = true,
  showPagination = true,
  onSelectionChange = () => {},
  onRowClick,
  enableRowSelection,
  rowClassName,
  containerClassName = '',
  tableClassName = '',
  paginateExpandedRows = true,
  columnPinning = EMPTY_PINNING,
}) {
  const [modifiedData, setModifiedData] = useState(data);
  const [columnVisibility, setColumnVisibility] = useState(defaultHidden);
  const [expanded, setExpanded] = useState({});
  const [draggedRowDetails, setDraggedRowDetails] = useState(null);
  const [columnSizing, setColumnSizing] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);

  const initialPinning = useMemo(() => {
    const left = [...(columnPinning?.left || [])];
    const right = [...(columnPinning?.right || [])];

    columns.forEach((col) => {
      const id = col.id || col.accessorKey;
      if (!id) return;
      if (
        (col.pinned === 'left' || col.sticky === 'left' || col.sticky === true || col.pinned === true) &&
        !left.includes(id)
      ) {
        left.push(id);
      } else if ((col.pinned === 'right' || col.sticky === 'right') && !right.includes(id)) {
        right.push(id);
      }
    });

    if ((selectable || draggable || hasExpand) && left.length > 0 && !left.includes('actions')) {
      left.unshift('actions');
    }

    return { left, right };
  }, [columns, columnPinning, selectable, draggable, hasExpand]);

  const [columnPinningState, setColumnPinningState] = useState(initialPinning);

  useEffect(() => {
    setColumnPinningState(initialPinning);
  }, [initialPinning]);

  // Store the latest callback in a ref to avoid re-triggering the effect
  // when the parent provides a new inline function each render
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  });

  useEffect(() => {
    onSelectionChangeRef.current(rowSelection);
  }, [rowSelection]);

  useEffect(() => {
    setModifiedData(data);
  }, [data]);

  let modifiedColumns = columns.map((col) => ({
    ...col,
    size: col.size || 80, // Default column width
    minSize: col.minSize || 80, // Minimum column width
    maxSize: col.maxSize || 500, // Maximum column width
  }));

  if (selectable || draggable || hasExpand) {
    modifiedColumns = [
      {
        id: 'actions',
        size: 40,
        minSize: 40,
        header: ({ table }) => (
          <div className="flex flex-col items-center justify-center">
            {draggable && (
              <div className="w-6 h-6 flex items-center justify-center">
                <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              </div>
            )}
            {selectable && (
              <div className="flex items-center justify-center">
                <IndeterminateCheckbox
                  {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomeRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler(),
                  }}
                />
              </div>
            )}
          </div>
        ),
        cell: ({ row }) => (
          // Keep expand/select/drag clicks from triggering onRowClick
          <div
            className="flex items-center justify-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {draggable && (
              <div className="flex items-center justify-center">
                <Button
                  onClick={() => {}}
                  className="cursor-grab active:cursor-grabbing focus:cursor-grab hover:bg-gray-50 rounded-md p-1 transition-colors"
                  startIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M2.4 8.75C2.4 9.4375 1.86 10 1.2 10C0.54 10 0 9.4375 0 8.75C0 8.0625 0.54 7.5 1.2 7.5C1.86 7.5 2.4 8.0625 2.4 8.75ZM1.2 3.75C0.54 3.75 0 4.3125 0 5C0 5.6875 0.54 6.25 1.2 6.25C1.86 6.25 2.4 5.6875 2.4 5C2.4 4.3125 1.86 3.75 1.2 3.75ZM1.2 0C0.54 0 0 0.5625 0 1.25C0 1.9375 0.54 2.5 1.2 2.5C1.86 2.5 2.4 1.9375 2.4 1.25C2.4 0.5625 1.86 0 1.2 0ZM4.8 2.5C5.46 2.5 6 1.9375 6 1.25C6 0.5625 5.46 0 4.8 0C4.14 0 3.6 0.5625 3.6 1.25C3.6 1.9375 4.14 2.5 4.8 2.5ZM4.8 3.75C4.14 3.75 3.6 4.3125 3.6 5C3.6 5.6875 4.14 6.25 4.8 6.25C5.46 6.25 6 5.6875 6 5C6 4.3125 5.46 3.75 4.8 3.75ZM4.8 7.5C4.14 7.5 3.6 8.0625 3.6 8.75C3.6 9.4375 4.14 10 4.8 10C5.46 10 6 9.4375 6 8.75C6 8.0625 5.46 7.5 4.8 7.5Z"
                        fill="#6B7280"
                      />
                    </svg>
                  }
                  variant="text"
                />
              </div>
            )}
            {selectable && (
              <div className="flex items-center justify-center">
                <IndeterminateCheckbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler(),
                  }}
                />
              </div>
            )}
            {row.getCanExpand() ? (
              <Button
                onClick={row.getToggleExpandedHandler()}
                className="cursor-pointer hover:bg-gray-50 rounded-md p-1 transition-colors"
                startIcon={
                  row.getIsExpanded() ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4.3349 6.87274C4.66033 6.5473 5.18797 6.5473 5.51341 6.87274L9.99999 11.3593L14.4866 6.87274C14.812 6.5473 15.3396 6.5473 15.6651 6.87274C15.9905 7.19818 15.9905 7.72581 15.6651 8.05125L10.5892 13.1271C10.2638 13.4525 9.73617 13.4525 9.41074 13.1271L4.3349 8.05125C4.00946 7.72581 4.00946 7.19818 4.3349 6.87274Z"
                        fill="#6B7280"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.87298 4.33465C6.54755 4.66009 6.54755 5.18773 6.87298 5.51317L11.3596 9.99975L6.87298 14.4863C6.54755 14.8118 6.54755 15.3394 6.87298 15.6648C7.19842 15.9903 7.72606 15.9903 8.0515 15.6648L13.1273 10.589C13.4528 10.2636 13.4528 9.73593 13.1273 9.41049L8.0515 4.33465C7.72606 4.00922 7.19842 4.00922 6.87298 4.33465Z"
                        fill="#6B7280"
                      />
                    </svg>
                  )
                }
                variant="text"
              />
            ) : null}
            {row.parentId ? (
              <div className="w-4 h-4 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <mask id="path-1-inside-1_40_6881" fill="white">
                    <rect
                      x="4"
                      y="4"
                      width="4.44945"
                      height="4.44944"
                      rx="0.333333"
                    />
                  </mask>
                  <rect
                    x="4"
                    y="4"
                    width="4.44945"
                    height="4.44944"
                    rx="0.333333"
                    stroke="#6B7280"
                    strokeWidth="2"
                    mask="url(#path-1-inside-1_40_6881)"
                  />
                  <path
                    d="M12.0508 15.5V12.0505H15.5002V15.5H12.0508Z"
                    stroke="#6B7280"
                  />
                  <path
                    d="M6.15723 8.3147V13.0413C6.15723 13.4095 6.4557 13.708 6.82389 13.708H11.5505"
                    stroke="#6B7280"
                  />
                </svg>
              </div>
            ) : null}
            {row?.subRows?.length ? (
              <div
                className="border border-gray-200 flex items-center gap-2 h-6 px-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={row.getToggleExpandedHandler()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <mask id="path-1-inside-1_40_6795" fill="white">
                    <rect
                      x="2"
                      y="2"
                      width="4.44945"
                      height="4.44944"
                      rx="0.333333"
                    />
                  </mask>
                  <rect
                    x="2"
                    y="2"
                    width="4.44945"
                    height="4.44944"
                    rx="0.333333"
                    stroke="#6B7280"
                    strokeWidth="2"
                    mask="url(#path-1-inside-1_40_6795)"
                  />
                  <path
                    d="M10.0508 13.5V10.0505H13.5002V13.5H10.0508Z"
                    stroke="#6B7280"
                  />
                  <path
                    d="M4.15723 6.3147V11.0413C4.15723 11.4095 4.4557 11.708 4.82389 11.708H9.5505"
                    stroke="#6B7280"
                  />
                </svg>
                <span className="text-xs font-medium text-gray-600">
                  {row?.subRows?.length}
                </span>
              </div>
            ) : null}
          </div>
        ),
      },
      ...modifiedColumns,
    ];
  }

  const handleDragStart = (e, rowIndex, row) => {
    setDraggedRowDetails(row);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, rowIndex) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex, targetRow) => {
    e.preventDefault();
    const isSubRow = !!targetRow?.parentId;
    if (isSubRow) {
      setModifiedData([
        ...swapRowsInData(modifiedData, targetRow, draggedRowDetails),
      ]);
    } else {
      setModifiedData([
        ...swapRootRowsInData(modifiedData, targetRow, draggedRowDetails),
      ]);
    }
  };

  const table = useReactTable({
    data: modifiedData,
    columns: modifiedColumns,
    // initialState: { pageIndex, pageSize: 10 },
    initialState: {
      pagination: { pageSize: showPagination ? pageSize : 1000 },
    },
    state: {
      columnVisibility,
      expanded,
      columnSizing,
      rowSelection,
      columnPinning: columnPinningState,
    },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinningState,
    enablePinning: true,
    enableRowSelection: enableRowSelection || true,
    getRowId: (row) => row.id,
    getSubRows: (row) => row?.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    paginateExpandedRows,
  });

  const leftPinnedColumns = table.getLeftLeafColumns();
  const lastLeftPinnedColumnId =
    leftPinnedColumns.length > 0
      ? leftPinnedColumns[leftPinnedColumns.length - 1]?.id
      : null;
  const rightPinnedColumns = table.getRightLeafColumns();
  const firstRightPinnedColumnId =
    rightPinnedColumns.length > 0 ? rightPinnedColumns[0]?.id : null;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${containerClassName}`}>
      <div
        className={`overflow-x-auto ${tableClassName}`}
        onScroll={(e) => {
          const scrolled = e.currentTarget.scrollLeft > 2;
          if (scrolled !== isScrolled) {
            setIsScrolled(scrolled);
          }
        }}
      >
        <table
          className="w-full table-fixed"
          style={{
            minWidth: table.getTotalSize() ? `${table.getTotalSize()}px` : '100%',
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => {
                  const isPinned = header.column.getIsPinned();
                  const isLastLeftPinned =
                    isPinned === 'left' && header.column.id === lastLeftPinnedColumnId;
                  const isFirstRightPinned =
                    isPinned === 'right' && header.column.id === firstRightPinnedColumnId;

                  const pinStyle = isPinned
                    ? {
                        position: 'sticky',
                        left:
                          isPinned === 'left'
                            ? `${header.column.getStart('left')}px`
                            : undefined,
                        right:
                          isPinned === 'right'
                            ? `${header.column.getStart('right')}px`
                            : undefined,
                        zIndex: 20,
                      }
                    : {};

                  return (
                    <th
                      className={`px-4 py-3 font-semibold text-left bg-[#151C2E] text-white border-b border-gray-200 relative ${
                        !isPinned &&
                        header.column.columnDef.header === 'Sl. No' &&
                        !header.column.columnDef.size
                          ? 'flex-[0.5] !pr-0 !w-12'
                          : 'flex-1'
                      } ${
                        isLastLeftPinned && isScrolled
                          ? 'shadow-[4px_0_8px_-2px_rgba(0,0,0,0.25)]'
                          : ''
                      } ${
                        isFirstRightPinned && isScrolled
                          ? 'shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.25)]'
                          : ''
                      }`}
                      key={header.id}
                      style={{
                        width: header.getSize(),
                        minWidth:
                          header.column.columnDef.minSize || header.getSize(),
                        ...pinStyle,
                      }}
                    >
                    <div
                      className={`flex items-center justify-between min-w-0 `}
                    >
                      <div
                        className={`text-base font-medium truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </div>
                      {showColumnSelector &&
                      i === headerGroup.headers.length - 1 ? (
                        <div className="flex-shrink-0 ml-2">
                          <Dropdown
                            buttonClassName="!bg-[#151C2E] hover:!bg-[#1a2333] text-white border-white/20"
                            chevron={false}
                            title={
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M6.26366 3.46983C6.91924 3.46983 7.45069 4.00128 7.45069 4.65683C7.45069 5.31239 6.91925 5.84383 6.26366 5.84384C5.60807 5.84384 5.07662 5.31239 5.07662 4.65683C5.07662 4.00128 5.60807 3.46983 6.26366 3.46983ZM8.97447 4.65683C8.97447 3.1597 7.76079 1.94604 6.26365 1.94604C5.03118 1.94604 3.9908 2.76853 3.66148 3.89457L2.21049 3.89457C1.78971 3.89457 1.4486 4.23568 1.4486 4.65646C1.4486 5.07724 1.78971 5.41835 2.21049 5.41835L3.66127 3.89457C3.99034 6.54477 5.03091 7.36762 6.26366 7.36762C7.76079 7.36762 8.97447 6.15397 8.97447 4.65683ZM8.55072 11.3427C8.55072 11.9983 9.08217 12.5297 9.73775 12.5297C10.3933 12.5297 10.9248 11.9983 10.9248 11.3427C10.9248 10.6872 10.3933 10.1557 9.73775 10.1557C9.08216 10.1557 8.55072 10.1552 8.55072 11.3427ZM9.73775 14.0535C8.24062 14.0535 7.02694 12.8399 7.02694 11.3427C7.02694 9.84558 8.24062 8.63193 9.73775 8.63193C10.9705 8.63193 12.0111 9.45477 12.3401 10.5812H13.7909C14.2117 10.5812 14.5528 10.9223 14.5528 11.3431C14.5528 11.7639 14.2117 12.105 13.7909 12.105H12.3399C12.0106 13.231 10.9702 14.0535 9.73775 14.0535ZM2.21013 10.5812C1.78935 10.5812 1.44824 10.9223 1.44824 11.3431C1.44824 11.7639 1.78935 12.105 2.21013 12.105L6.01433 12.105C6.43511 12.105 6.77622 11.7639 6.77622 11.3431C6.77622 10.9223 6.43511 10.5812 6.01433 10.5812H2.21013ZM14.553 4.65646C14.553 5.07724 14.2119 5.41835 13.7911 5.41835L9.9869 5.41835C9.56612 5.41835 9.22501 4.23568 9.22501 4.65646C9.22501 4.23568 9.56612 3.89457 9.9869 3.89457L13.7911 3.89457C14.2119 3.89457 14.553 4.23568 14.553 4.65646Z"
                                  fill="white"
                                />
                              </svg>
                            }
                            orientation="bottom"
                            dropdownClassName="z-[9999] max-h-60 overflow-y-auto bg-white shadow-lg border border-gray-200 rounded-md min-w-48"
                          >
                            {table.getAllLeafColumns().map((column) => {
                              return (
                                <div key={column.id} className="px-3 py-2">
                                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors">
                                    <IndeterminateCheckbox
                                      {...{
                                        type: 'checkbox',
                                        checked: column.getIsVisible(),
                                        onChange:
                                          column.getToggleVisibilityHandler(),
                                      }}
                                    />
                                    <span className="text-base text-gray-700">
                                      {flexRender(column.columnDef.header, {
                                        table,
                                        column,
                                      })}
                                    </span>
                                  </label>
                                </div>
                              );
                            })}
                          </Dropdown>
                        </div>
                      ) : null}
                    </div>
                    {/* Column resize handle */}
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={`absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none ${
                        header.column.getIsResizing()
                          ? 'bg-blue-500'
                          : 'bg-transparent'
                      } hover:bg-blue-400 transition-colors`}
                    />
                  </th>
                );
              })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              return (
                <tr
                  key={row.id}
                  className={`bg-white border-b border-gray-100 group hover:bg-gray-50 transition-colors duration-150 ${
                    typeof rowClassName === 'function'
                      ? rowClassName(row.original)
                      : rowClassName || ''
                  }`}
                  draggable={draggable}
                  onDragStart={(e) => handleDragStart(e, index, row)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index, row)}
                  onDragEnd={() => {
                    setDraggedRowDetails(null);
                  }}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  style={{
                    flexGrow: index === draggedRowDetails?.index ? 2 : 1,
                    cursor: onRowClick ? 'pointer' : 'default',
                    opacity:
                      draggedRowDetails && draggedRowDetails.id === row.id
                        ? 0.5
                        : 1,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnSize = cell.column.getSize();
                    const isPinned = cell.column.getIsPinned();
                    const isLastLeftPinned =
                      isPinned === 'left' && cell.column.id === lastLeftPinnedColumnId;
                    const isFirstRightPinned =
                      isPinned === 'right' && cell.column.id === firstRightPinnedColumnId;

                    const pinStyle = isPinned
                      ? {
                          position: 'sticky',
                          left:
                            isPinned === 'left'
                              ? `${cell.column.getStart('left')}px`
                              : undefined,
                          right:
                            isPinned === 'right'
                              ? `${cell.column.getStart('right')}px`
                              : undefined,
                          zIndex: 10,
                        }
                      : {};

                    return (
                      <td
                        className={`${
                          cell?.row.parentId ? 'pl-8' : 'pl-4'
                        } py-3 pr-4 ${
                          isPinned
                            ? 'bg-white group-hover:bg-gray-50 transition-colors duration-150'
                            : ''
                        } ${
                          isLastLeftPinned && isScrolled
                            ? 'shadow-[4px_0_8px_-2px_rgba(0,0,0,0.06)]'
                            : ''
                        } ${
                          isFirstRightPinned && isScrolled
                            ? 'shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)]'
                            : ''
                        }`}
                        key={cell.id}
                        title={
                          typeof cell.getValue() === 'string' ||
                          typeof cell.getValue() === 'number'
                            ? String(cell.getValue())
                            : ''
                        }
                        style={{
                          width: columnSize,
                          minWidth: cell.column.columnDef.minSize || columnSize,
                          ...pinStyle,
                        }}
                      >
                        <div className={`text-base text-gray-900 ${
                          typeof cell.column.columnDef.meta?.containerClassName === 'function'
                            ? cell.column.columnDef.meta.containerClassName(cell.getContext())
                            : cell.column.columnDef.meta?.containerClassName || 'truncate overflow-hidden text-ellipsis whitespace-nowrap'
                        }`}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!data?.length ? (
        <div className="flex items-center justify-center w-full py-16 text-base text-gray-500 bg-gray-50">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-xl font-medium text-gray-900 mb-2">
              No data available
            </p>
            <p className="text-base text-gray-500">
              There are no records to display at the moment.
            </p>
          </div>
        </div>
      ) : (
        showPagination && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-2 text-base text-gray-600">
              <span>Page</span>
              <input
                disabled
                type="number"
                value={pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                className="w-16 px-3 py-1 text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                className="px-3 py-2 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={() => {
                  if (!table.getCanPreviousPage() && !isFirst) {
                    fetchPreviousPage();
                  }
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage() && isFirst}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <button
                className="px-3 py-2 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={() => {
                  if (table.getCanNextPage() || !isLast) {
                    fetchNextPage();
                  }
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage() && isLast}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

SimpleTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  selectable: PropTypes.bool,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  draggable: PropTypes.bool,
  canPreviousPage: PropTypes.bool,
  hasExpand: PropTypes.bool,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  fetchNextPage: PropTypes.func,
  fetchPreviousPage: PropTypes.func,
  defaultHidden: PropTypes.object,
  showColumnSelector: PropTypes.bool,
  showPagination: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onRowClick: PropTypes.func,
  enableRowSelection: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  containerClassName: PropTypes.string,
  tableClassName: PropTypes.string,
  paginateExpandedRows: PropTypes.bool,
  columnPinning: PropTypes.shape({
    left: PropTypes.arrayOf(PropTypes.string),
    right: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default SimpleTable;
